import React, { useState, useEffect } from 'react';
import { Calendar } from './ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Clock, MapPin, Calendar as CalendarIcon } from 'lucide-react';
import axios from 'axios';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CourseCalendar = ({ courseId, courseTitle, onDateSelect }) => {
  const [courseDates, setCourseDates] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCourseDates();
  }, [courseId]);

  const fetchCourseDates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/course-dates/course/${courseId}`);
      setCourseDates(response.data);
    } catch (error) {
      console.error('Error fetching course dates:', error);
    } finally {
      setLoading(false);
    }
  };

  const availableDates = courseDates.map(cd => new Date(cd.date));
  
  const isDateAvailable = (date) => {
    return availableDates.some(availableDate => 
      availableDate.toDateString() === date.toDateString()
    );
  };

  const getDateInfo = (date) => {
    return courseDates.find(cd => 
      new Date(cd.date).toDateString() === date.toDateString()
    );
  };

  const handleDateSelect = (date) => {
    if (isDateAvailable(date)) {
      const dateInfo = getDateInfo(date);
      setSelectedDate(dateInfo);
      if (onDateSelect) {
        onDateSelect(dateInfo);
      }
    }
  };

  const modifiers = {
    available: availableDates,
    selected: selectedDate ? [new Date(selectedDate.date)] : []
  };

  const modifiersStyles = {
    available: {
      backgroundColor: '#FFD700',
      color: 'white',
      fontWeight: 'bold'
    },
    selected: {
      backgroundColor: '#D4AF37',
      color: 'white',
      fontWeight: 'bold'
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-bold text-gray-900 mb-2">Wähle einen Termin</h3>
        <p className="text-sm text-gray-600 mb-4">
          Verfügbare Termine sind in <span className="text-[#D4AF37] font-semibold">Gold</span> markiert
        </p>
      </div>

      {/* Available Dates List */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Alle verfügbaren Termine</h4>
        <div className="grid gap-3">
          {courseDates.map((courseDate) => (
            <Card 
              key={courseDate.id}
              className={`cursor-pointer transition-all duration-200 hover:border-[#D4AF37] ${
                selectedDate?.id === courseDate.id ? 'border-2 border-[#D4AF37] bg-[#D4AF37]/5' : 'border'
              }`}
              onClick={() => {
                setSelectedDate(courseDate);
                onDateSelect(courseDate);
              }}
            >
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col">
                      <span className="text-sm font-semibold">
                        {format(new Date(courseDate.date), 'dd. MMM yyyy', { locale: de })}
                      </span>
                      <span className="text-xs text-gray-600">
                        {courseDate.time || '09:30'} Uhr
                      </span>
                    </div>
                  </div>
                  {courseDate.is_full && (
                    <Badge variant="destructive" className="text-xs">Ausgebucht</Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>

      {/* Calendar View */}
      <div className="grid md:grid-cols-2 gap-6">
        <Card>
          <CardContent className="pt-6">
            <Calendar
              mode="single"
              selected={selectedDate ? new Date(selectedDate.date) : undefined}
              onSelect={handleDateSelect}
              modifiers={modifiers}
              modifiersStyles={modifiersStyles}
              locale={de}
              disabled={(date) => !isDateAvailable(date) || date < new Date()}
              className="rounded-md border"
            />
          </CardContent>
        </Card>

        {/* Date Details */}
        <div>
          {selectedDate && (
            <Card className="border-2 border-[#D4AF37]">
              <CardHeader className="bg-gradient-to-r from-[#D4AF37]/10 to-[#FFD700]/10">
                <CardTitle className="text-[#D4AF37]">
                  Ausgewählter Termin
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6 space-y-4">
                <div className="flex items-center space-x-3">
                  <CalendarIcon className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-gray-600">Datum</p>
                    <p className="font-semibold">
                      {format(new Date(selectedDate.date), 'EEEE, dd. MMMM yyyy', { locale: de })}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Clock className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-gray-600">Uhrzeit</p>
                    <p className="font-semibold">
                      {selectedDate.time || '09:30'} Uhr
                      {courseId === '8' ? ' - 21:00 Uhr' : ' - 17:00 Uhr'}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  <div>
                    <p className="text-sm text-gray-600">Ort</p>
                    <p className="font-semibold text-sm">{selectedDate.location}</p>
                  </div>
                </div>

                <div className="pt-4 border-t">
                  <Button
                    onClick={() => onDateSelect(selectedDate)}
                    disabled={selectedDate.is_full}
                    className="w-full bg-gradient-to-r from-[#D4AF37] to-[#FFD700] hover:from-[#B8960C] hover:to-[#D4AF37] text-white"
                  >
                    {selectedDate.is_full ? 'Ausgebucht' : 'Weiter zur Anmeldung'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};
